import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { useRef } from 'react';

/**
 * Horizontal padding to ensure the next tab is partially visible,
 * so that users know there are more tabs.
 */
const SCROLL_PADDING = 42;

/**
 * Utility hook to perform scrolling to ensure selected tab is visible in the viewport
 */
const useThumbnailBarAutoScroll = () => {
  const tabsRef = useRef<Map<number, HTMLDivElement> | null>(null);
  function getTabsMap() {
    if (!tabsRef.current) {
      tabsRef.current = new Map();
    }
    return tabsRef.current;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<OverlayScrollbarsComponentRef | null>(null);

  const performScrolling = (selectedIndex: number) => {
    const container = containerRef.current;
    const viewport = scrollAreaRef.current?.osInstance()?.elements().viewport;
    const selectedTab = getTabsMap().get(selectedIndex);

    if (!container || !viewport || !selectedTab) return;

    const containerRect = container.getBoundingClientRect();
    const rect = selectedTab.getBoundingClientRect();
    const leftOffset = rect.left - containerRect.left - SCROLL_PADDING;
    const rightOffset = rect.right - containerRect.left + SCROLL_PADDING;

    const { scrollLeft: currentScrollLeft, clientWidth: viewportWidth } =
      viewport;

    if (leftOffset < currentScrollLeft) {
      viewport.scrollLeft = leftOffset;
    } else if (rightOffset > currentScrollLeft + viewportWidth) {
      viewport.scrollLeft = rightOffset - viewportWidth;
    }
  };

  return {
    containerRef,
    scrollAreaRef,
    performScrolling,
    getTabRefCallback: (index: number) => (node: HTMLDivElement | null) => {
      if (node) getTabsMap().set(index, node);
    },
  };
};

export default useThumbnailBarAutoScroll;
